import type { Role, RoleAssignment, RoleProvenance } from '@ralph/contracts'
import clsx from 'clsx'
import { Building2, Clock, KeyRound, UserRound } from 'lucide-react'
import type { ReactNode } from 'react'
import { type I18n, useI18n } from '@/i18n/index.ts'

/**
 * A role, and where it came from.
 *
 * Provenance is the most important thing on this plane, because it decides whether the next
 * action is possible here at all: a granted role has a Remove control, a directory role has a
 * different one that writes an override, and a role that arrived from `AUTH_PROVIDERS_JSON` has
 * none and says where it does live.
 *
 * The mark is never colour alone (AGM-124). Each provenance carries its own glyph and its own
 * word, and the tint only reinforces them — which also means the chips stay legible in both
 * themes without either palette having to carry meaning it was not picked for.
 */

const ICONS: Record<RoleProvenance, ReactNode> = {
  // The person glyph, not the people one: groups own `users-round` (PRD-26 *The group avatar*).
  floor: <UserRound size={11} aria-hidden />,
  directory: <Building2 size={11} aria-hidden />,
  configuration: <Building2 size={11} aria-hidden />,
  grant: <KeyRound size={11} aria-hidden />
}

/** `grant` is the only provenance an administrator can act on, so it is the only one that lifts. */
const TONE: Record<RoleProvenance, string> = {
  floor: 'border-rule text-fg-subtle',
  directory: 'border-rule text-fg-muted',
  configuration: 'border-rule text-fg-muted',
  grant: 'border-rule-strong bg-card-solid font-semibold text-fg'
}

/**
 * `fmt` is a parameter because this is a pure function two components and a test call. Taking
 * the locale-bound bag — the words as well as the formatter — rather than reaching for one keeps
 * it callable from all three, and keeps the language the caller's: a catalogue read at module
 * scope would freeze it on a switch, with no type error and no failing test (PRD-27 F-3).
 */
export const provenanceSentence = (fmt: Pick<I18n, 'formatAgo' | 't'>, assignment: RoleAssignment): string => {
  const t = fmt.t.people
  switch (assignment.provenance) {
    case 'floor':
      return t.provenance.floor
    case 'directory':
      return t.provenance.directory
    case 'configuration':
      return t.provenance.configuration
    case 'grant':
      return assignment.pendingApproval
        ? t.provenance.grantPending
        : t.provenance.grant(assignment.grantedByName, fmt.formatAgo(assignment.grantedAt ?? ''))
  }
}

/**
 * The compact form, for a directory row.
 *
 * One chip per role — not per source — because a row is scanned, not read: somebody looking for
 * the administrators wants to see `Administrator` once. The profile is where a role held twice
 * is explained twice.
 *
 * Only what is in force. A role a deny is subtracting, and a grant still waiting for a second
 * administrator, are both absent — which is what those states mean. The profile is where they are
 * listed, marked, and explained; a row that claims to show the effective set may not quietly
 * include things nobody can use.
 */
export const RoleChipRow = ({ roles }: { roles: RoleAssignment[] }) => {
  const fmt = useI18n()
  const t = fmt.t.people
  const seen = new Map<Role, RoleAssignment>()

  // Filtered BEFORE the dedupe, not after: a role held through the directory and also carrying a
  // grant that is waiting for approval would otherwise pick the grant as its representative and
  // then vanish, taking a chip the person genuinely has with it.
  for (const assignment of roles.filter((role) => role.effective)) {
    // A grant outranks a directory copy in the row, because it is the actionable one.
    const held = seen.get(assignment.role)
    if (!held || (assignment.provenance === 'grant' && held.provenance !== 'grant')) {
      seen.set(assignment.role, assignment)
    }
  }

  const shown = [...seen.values()].filter((assignment) => assignment.role !== 'user' || seen.size === 1)

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((assignment) => (
        <span
          key={`${assignment.role}-${assignment.provenance}`}
          title={provenanceSentence(fmt, assignment)}
          className={clsx(
            'inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[11.5px] tracking-[-0.01em]',
            TONE[assignment.provenance],
            assignment.pendingApproval && 'opacity-60'
          )}
        >
          {ICONS[assignment.provenance]}
          {t.roleLabel[assignment.role]}
          {assignment.expiresAt && <Clock size={10} aria-hidden />}
        </span>
      ))}
    </div>
  )
}

/**
 * The full form, for a profile: one line per source, with its own control.
 *
 * A role that is both granted here and sent by the directory appears twice, on purpose.
 * Removing the grant leaves the directory's copy, and an administrator who could not see both
 * would read that as the product ignoring them.
 */
export const RoleDetailRow = ({ assignment, action }: { assignment: RoleAssignment; action?: ReactNode }) => {
  const fmt = useI18n()
  const t = fmt.t.people

  return (
    <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5 border-t border-rule py-2.5 first:border-t-0">
      <div className="min-w-0 flex-1 basis-[13rem]">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={clsx(
              'inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[12px]',
              TONE[assignment.provenance],
              // Not colour alone (AGM-124): the sentence underneath says the same thing in words.
              !assignment.effective && 'opacity-55'
            )}
          >
            {ICONS[assignment.provenance]}
            {t.roleLabel[assignment.role]}
          </span>
          <span className="readout text-fg-subtle">{t.provenance.label[assignment.provenance]}</span>
          {assignment.expiresAt && (
            <span className="readout inline-flex items-center gap-1 text-fg-subtle">
              <Clock size={10} aria-hidden />
              {t.provenance.expires(fmt.formatUntil(assignment.expiresAt))}
            </span>
          )}
        </div>

        <p className="mt-1 text-[12.5px] leading-[1.5] text-fg-muted">{provenanceSentence(fmt, assignment)}</p>
        {!assignment.effective && !assignment.pendingApproval && (
          <p className="mt-0.5 text-[12.5px] leading-[1.5] text-alert">{t.provenance.overridden}</p>
        )}
        {assignment.reason !== '' && (
          <p className="mt-0.5 text-[12.5px] italic leading-[1.5] text-fg-subtle">
            {t.provenance.reason(assignment.reason)}
          </p>
        )}
      </div>

      {action && <div className="flex shrink-0 items-center gap-1.5">{action}</div>}
    </div>
  )
}
